import { createPortal } from 'react-dom'
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Clock3 } from 'lucide-react'

interface CustomTimePickerProps {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  buttonClassName?: string
  dropdownClassName?: string
  calendarWidth?: number
  placement?: 'down' | 'up' | 'auto'
  align?: 'start' | 'end'
}

function formatTimeLabel(value: string) {
  if (!value) {
    return ''
  }

  const [hoursRaw, minutes = '00'] = value.split(':')
  const hours = Number(hoursRaw)

  if (!Number.isFinite(hours)) {
    return value
  }

  const suffix = hours >= 12 ? 'PM' : 'AM'
  const normalizedHours = hours % 12 || 12

  return `${String(normalizedHours).padStart(2, '0')}:${minutes} ${suffix}`
}

export function CustomTimePicker({
  label,
  value,
  onChange,
  placeholder = 'Select time',
  className = '',
  buttonClassName = '',
  dropdownClassName = '',
  calendarWidth = 232,
  placement = 'auto',
  align = 'start',
}: CustomTimePickerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const [hour, setHour] = useState('12')
  const [minute, setMinute] = useState('00')
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM')
  const [open, setOpen] = useState(false)
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({})

  // Initialize values when value prop changes
  useEffect(() => {
    if (value) {
      const isPm = value.toUpperCase().includes('PM')
      const isAm = value.toUpperCase().includes('AM')
      const cleanValue = value.replace(/(AM|PM|\s)/gi, '')
      const [hRaw, mRaw] = cleanValue.split(':')
      let hNum = Number(hRaw) || 0
      if (isPm && hNum < 12) hNum += 12
      if (isAm && hNum === 12) hNum = 0
      const mStr = String(mRaw || '00').padStart(2, '0').slice(0, 2)
      
      const p = hNum >= 12 ? 'PM' : 'AM'
      const h12 = hNum % 12 || 12
      const hStr = String(h12).padStart(2, '0')
      
      setHour(hStr)
      setMinute(mStr)
      setPeriod(p)
    } else {
      setHour('12')
      setMinute('00')
      setPeriod('AM')
    }
  }, [value])

  const openTimePicker = () => {
    if (value) {
      const isPm = value.toUpperCase().includes('PM')
      const isAm = value.toUpperCase().includes('AM')
      const cleanValue = value.replace(/(AM|PM|\s)/gi, '')
      const [hRaw, mRaw] = cleanValue.split(':')
      let hNum = Number(hRaw) || 0
      if (isPm && hNum < 12) hNum += 12
      if (isAm && hNum === 12) hNum = 0
      const mStr = String(mRaw || '00').padStart(2, '0').slice(0, 2)

      const p = hNum >= 12 ? 'PM' : 'AM'
      const h12 = hNum % 12 || 12
      const hStr = String(h12).padStart(2, '0')

      setHour(hStr)
      setMinute(mStr)
      setPeriod(p)
    }
    setOpen((prev) => !prev)
  }

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node

      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [])

  const updatePanelPosition = () => {
    const rect = rootRef.current?.getBoundingClientRect()
    if (!rect) return

    const panelWidth = calendarWidth
    const actualHeight = panelRef.current?.offsetHeight || 160
    const gap = 8
    const sidePadding = 12

    const isPlacementUp =
      placement === 'up' ||
      (placement !== 'down' && (rect.bottom + gap + actualHeight > window.innerHeight - sidePadding))

    const preferredTop = isPlacementUp ? rect.top - gap - actualHeight : rect.bottom + gap
    const fallbackTop = isPlacementUp ? rect.bottom + gap : rect.top - gap - actualHeight
    const hasRoomInPlacement = isPlacementUp
      ? preferredTop >= sidePadding
      : preferredTop + actualHeight <= window.innerHeight - sidePadding

    const top = Math.max(
      sidePadding,
      Math.min(hasRoomInPlacement ? preferredTop : fallbackTop, window.innerHeight - actualHeight - sidePadding),
    )

    const preferredLeft = align === 'end' ? rect.right - panelWidth : rect.left
    const left = Math.max(sidePadding, Math.min(preferredLeft, window.innerWidth - panelWidth - sidePadding))

    setPanelStyle({
      left,
      top,
      width: panelWidth,
    })
  }

  useEffect(() => {
    if (!open) return

    updatePanelPosition()
    const raf = requestAnimationFrame(updatePanelPosition)

    window.addEventListener('resize', updatePanelPosition)
    window.addEventListener('scroll', updatePanelPosition, true)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', updatePanelPosition)
      window.removeEventListener('scroll', updatePanelPosition, true)
    }
  }, [open, calendarWidth, placement, align])

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, '')
    if (val.length > 2) val = val.slice(0, 2)
    const num = Number(val)
    if (val === '' || (num >= 0 && num <= 23)) {
      setHour(val)
    }
  }

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, '')
    if (val.length > 2) val = val.slice(0, 2)
    const num = Number(val)
    if (val === '' || (num >= 0 && num <= 59)) {
      setMinute(val)
    }
  }

  const handleCancel = () => {
    if (value) {
      const [hRaw, mRaw] = value.split(':')
      const hNum = Number(hRaw) || 0
      const mStr = String(mRaw || '00').padStart(2, '0')
      const p = hNum >= 12 ? 'PM' : 'AM'
      const h12 = hNum % 12 || 12
      const hStr = String(h12).padStart(2, '0')
      setHour(hStr)
      setMinute(mStr)
      setPeriod(p)
    } else {
      setHour('12')
      setMinute('00')
      setPeriod('AM')
    }
    setOpen(false)
  }

  const handleOk = () => {
    let hNum = Number(hour) || 0
    let mNum = Number(minute) || 0

    hNum = Math.min(Math.max(hNum, 0), 23)
    mNum = Math.min(Math.max(mNum, 0), 59)

    let finalHour = hNum

    if (hNum >= 1 && hNum <= 12) {
      if (period === 'PM' && hNum < 12) {
        finalHour = hNum + 12
      } else if (period === 'AM' && hNum === 12) {
        finalHour = 0
      }
    }

    const formattedHour = String(finalHour).padStart(2, '0')
    const formattedMinute = String(mNum).padStart(2, '0')

    onChange(`${formattedHour}:${formattedMinute}`)
    setOpen(false)
  }

  const selectedLabel = useMemo(() => value ? formatTimeLabel(value) : placeholder, [value, placeholder])

  const renderDropdown = () => {
    if (!open || typeof document === 'undefined') return

    return createPortal(
      <div
        ref={panelRef}
        style={panelStyle}
        className={`fixed z-[200] rounded-2xl border border-[#dfe7f0] bg-white p-4 shadow-xl shadow-slate-900/10 ${dropdownClassName}`}
      >
        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 block">
          Enter time
        </span>
        <div className="flex gap-2">
          <input
            type="text"
            value={hour}
            onChange={handleHourChange}
            placeholder="12"
            className="w-12 h-10 text-center rounded-xl border border-[#cbd5e1] text-sm font-bold text-slate-800 outline-none focus:border-[#146971]"
          />
          <span className="text-slate-400 font-bold self-center">:</span>
          <input
            type="text"
            value={minute}
            onChange={handleMinuteChange}
            placeholder="00"
            className="w-12 h-10 text-center rounded-xl border border-[#cbd5e1] text-sm font-bold text-slate-800 outline-none focus:border-[#146971]"
          />
          <button
            type="button"
            onClick={() => setPeriod((prev) => (prev === 'AM' ? 'PM' : 'AM'))}
            className="h-10 px-3 rounded-xl border border-[#cbd5e1] text-xs font-bold text-[#146971] bg-slate-50 hover:bg-slate-100 transition"
          >
            {period}
          </button>
        </div>
        <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-3 py-1.5 text-[12px] font-bold text-slate-500 hover:text-slate-800 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleOk}
            className="px-3 py-1.5 text-[12px] font-bold text-[#146971] hover:text-[#0f565d] transition"
          >
            OK
          </button>
        </div>
      </div>,
      document.body,
    )
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {label ? <span className="mb-2 block text-[13px] font-semibold text-[#64748b]">{label}</span> : null}
      <button
        type="button"
        onClick={openTimePicker}
        className={`relative flex h-10 w-full items-center rounded-[8px] border border-[#dfe7f0] bg-white px-4 pr-10 text-left text-[14px] font-normal text-[#111827] outline-none transition focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10 hover:border-[#146971]/60 ${buttonClassName}`}
      >
        <span className={`min-w-0 flex-1 truncate text-left ${value ? 'text-[#111827]' : 'text-[#9aa3af]'}`}>
          {selectedLabel}
        </span>
        <Clock3 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 shrink-0 text-[#7c8aa3]" />
      </button>
      {renderDropdown()}
    </div>
  )
}
